import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { environment } from '~/config/env';
import {
    AuthContextInterface,
    DocumentViewerProviderInterface,
} from '~/interfaces/contexts';
import {
    CGDocumentInterfaceDetailed,
    CGDocumentInternalMetadata,
    CGDocumentMetadata,
    CGDocumentQueryParameters,
} from '~/interfaces/entities';
import { utils } from '~/utils';
import { getQueryParams } from '~/utils/getQueryParams';
import { useEntityContext } from '../../hooks';
import { commentsServices, documentServices } from '../../services';
import { getErrorCode } from '../../utils/getErrorCode';
import { AuthContext } from '~/context/auth';

export const DocumentViewerContext =
    createContext<DocumentViewerProviderInterface | null>(null);

DocumentViewerContext.displayName = 'DocumentViewerContext';

const queryParams = getQueryParams<CGDocumentQueryParameters>('smartViewer');

const DocumentViewerProvider = ({ children }: PropsWithChildren) => {
    const intl = useIntl();

    const { logout, user } =
        useEntityContext<AuthContextInterface>(AuthContext);

    const [showViewer, setShowViewer] = useState(true);

    const [viewerWidth, setViewerWidth] = useState<number>(650);

    /*
        This state stores the basic information to start displaying the document.
        Such as the the URL, the initial page, text to be highlighted, etc. 
        */
    const [documentMetadata, setDocumentMetadata] =
        useState<CGDocumentInternalMetadata | null>(null);

    /*
       This state stores the information of the document that is being displayed in the viewer.
       Such as the document itself, the file id, tags, etc.
       */
    const [documentDisplayData, setDocumentDisplayData] =
        useState<CGDocumentInterfaceDetailed | null>(null);

    const [loading, setLoading] = useState(false);

    const getDocument = async (documentData: CGDocumentMetadata) => {
        setShowViewer(true);
        setLoading(true);

        const response = await documentServices.getDocument(documentData.id);

        if (response.error) {
            toast.error(
                intl.formatMessage({ id: 'document_no_exist_or_forbidden' })
            );
            setLoading(false);

            return;
        }

        const { document: doc } = response;

        // we need to be sure that the token is not expired, if it is, renew it before continue
        const result = await utils.isTokenExpired();

        if (result.sessionHasExpired) return logout();

        const url = `${environment.apiUrl}/files/${doc.file_id}/stream`;
        const payload: CGDocumentInternalMetadata = {
            PDFUrl: url,
            fileId: doc.file_id,
            userId: user?.id,
            initialPage: documentData.initialPage,
            markedText: documentData.text,
            bearerToken: result.token,
        };

        setLoading(false);

        setDocumentDisplayData(doc);
        setDocumentMetadata(payload);
    };

    const onDeleteDocument = async () => {
        setLoading(true);

        if (!documentDisplayData) return;

        const [error, response] = await documentServices.deleteFiles([
            documentDisplayData.id,
        ]);

        setLoading(false);

        if (error) {
            toast.error(
                intl.formatMessage({ id: getErrorCode(response.error_code) })
            );
            return;
        }

        toast.success(intl.formatMessage({ id: 'doc_deleted_successfully' }));
        resetViewer();
    };

    const resetViewer = () => {
        setDocumentMetadata(null);
        setDocumentDisplayData(null);
    };

    useEffect(() => {
        if (queryParams) {
            setParams();
        }
    }, []);

    const setParams = async () => {
        if (!queryParams) return;

        let parsedParams: CGDocumentMetadata;

        // if comment id is included in the query params, we need to get the full info of the comment
        if (queryParams.commentId) {
            const [error, response] = await commentsServices.getCommentById(
                queryParams.commentId
            );

            if (error) {
                toast.error(intl.formatMessage({ id: 'general_error' }));
                resetViewer();
                return;
            }

            const { data } = response;

            parsedParams = {
                id: data.document_id,
                initialPage: data.page_number,
            };
        } else {
            parsedParams = {
                id: queryParams.id || queryParams.documentId || '',
                file_id: queryParams.file_id || queryParams.fileId,
                ...queryParams,
            };
        }

        if (!parsedParams || !parsedParams.id) return;

        getDocument(parsedParams);
    };

    const handleViewDocument = (documentData: CGDocumentMetadata) => {
        setShowViewer(true);

        if (documentData.id === documentDisplayData?.id) {
            const textHasChanged =
                documentData.text !== documentMetadata?.markedText;

            const pageHasChanged =
                documentData.initialPage !== documentMetadata?.initialPage;

            if (textHasChanged || pageHasChanged) {
                setDocumentMetadata({
                    ...documentMetadata,
                    initialPage: documentData.initialPage,
                    markedText: documentData.text,
                });
            }

            return;
        }

        getDocument(documentData);
    };

    return (
        <DocumentViewerContext.Provider
            value={{
                loading,

                document: documentDisplayData,
                viewerWidth,
                documentMetadata,
                showViewer,

                setDocument: setDocumentDisplayData,
                onDeleteDocument,
                handleViewerWidth: setViewerWidth,
                resetViewer,
                handleViewDocument,
                onClose: () => setShowViewer(false),
            }}
        >
            {children}
        </DocumentViewerContext.Provider>
    );
};

export default DocumentViewerProvider;
